import { useState, useRef, useEffect } from "react";
import { usePortal } from "@/data/portal/context";
import type { PortalThreadMessage } from "@/data/portal/types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, User, Bot, Bell } from "lucide-react";
import { cn } from "@/lib/utils";

interface PortalCommunicationThreadProps {
  jobId: string;
  customerName: string;
}

/**
 * Unified communication thread for the customer portal.
 * Shows system updates, outbound messages, and supports inbound from customer.
 */
export function PortalCommunicationThread({ jobId, customerName }: PortalCommunicationThreadProps) {
  const { threadMessages, addThreadMessage, tenantId, branding } = usePortal();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const jobMessages = threadMessages
    .filter(m => m.jobId === jobId)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [jobMessages.length]);

  const handleSend = () => {
    if (!draft.trim()) return;
    const msg: PortalThreadMessage = {
      id: `ptm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      tenantId,
      jobId,
      direction: "inbound",
      channel: "portal",
      senderName: customerName,
      body: draft.trim(),
      timestamp: new Date().toISOString(),
      readByCustomer: true,
      readByInternal: false,
    };
    addThreadMessage(msg);
    setDraft("");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3 px-1 py-2 max-h-[320px]"
      >
        {jobMessages.length === 0 && (
          <div className="text-center py-8">
            <Bell className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">No messages yet</p>
            <p className="text-xs text-muted-foreground/60">
              Updates about your project will appear here.
            </p>
          </div>
        )}
        {jobMessages.map(msg => (
          <div
            key={msg.id}
            className={cn(
              "flex gap-2",
              msg.direction === "inbound" && "justify-end"
            )}
          >
            {msg.direction !== "inbound" && (
              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                {msg.direction === "system" ? (
                  <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </div>
            )}
            <div
              className={cn(
                "max-w-[80%] rounded-lg px-3 py-2",
                msg.direction === "inbound"
                  ? "bg-primary text-primary-foreground"
                  : msg.direction === "system"
                  ? "bg-muted/50 border border-border/40"
                  : "bg-card border border-border/40"
              )}
            >
              {msg.direction !== "inbound" && (
                <p className="text-[10px] font-medium text-muted-foreground mb-0.5">
                  {msg.senderName}
                </p>
              )}
              <p className="text-sm leading-relaxed">{msg.body}</p>
              <p
                className={cn(
                  "text-[10px] mt-1",
                  msg.direction === "inbound" ? "text-primary-foreground/60" : "text-muted-foreground/50"
                )}
              >
                {new Date(msg.timestamp).toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="border-t border-border/40 pt-3 mt-2">
        <div className="flex gap-2">
          <Textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Type a message..."
            className="min-h-[40px] max-h-[80px] resize-none text-sm"
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!draft.trim()}
            className="shrink-0 self-end"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
